import { useState, useMemo } from "react";
import { useRouter } from "next/router";
import LivePriceBox from "@/elements/LivePriceBox/LivePriceBox";
import NseOrBsePrice from "@/elements/NseOrBsePrice/NseOrBsePrice";
import { BsSearch } from "react-icons/bs";
import ascDescIcon from "@/assets/icons/ascDescIcon.png";
import orderDefault from "@/assets/icons/orderDefault.png";
import Image from "next/image";
import { cn } from "@/lib/utils";
import Actions from "@/elements/Actions";
import { LuSearchX } from "react-icons/lu";
import mixpanel from "mixpanel-browser";
import { Mixpanel } from "@/elements/Mixpanel";

export default function TopGainersLosersTable({ stocksDetail, fincodeData, data, loading, isStockPage = false }) {
    const [filter, setFilter] = useState("all");
    const [searchText, setSearchText] = useState("");
    const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });
    const router = useRouter();
    const topGainersLength = data.topGainers?.length || 0;
    const topLosersLength = data.topLosers?.length || 0;

    const getPriceChangePercentage = (stock) => {
        const stockData = fincodeData?.[stock];
        const nseChange =
            stockData?.nseLtpPrice && stockData?.nseClosePrice
                ? ((stockData?.nseLtpPrice - stockData?.nseClosePrice) / stockData?.nseClosePrice) * 100
                : null;
        const bseChange =
            stockData?.bseLtpPrice && stockData?.bseClosePrice
                ? ((stockData?.bseLtpPrice - stockData?.bseClosePrice) / stockData?.bseClosePrice) * 100
                : null;

        return nseChange || bseChange || 0;
    };

    const handleSort = (key) => {
        setSortConfig((prev) => ({
            key,
            direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc"
        }));
    };

    const getSortIcon = (key) => {
        if (sortConfig.key === key) {
            return ascDescIcon;
        }
        return orderDefault;
    };

    const sortedData = (dataList) => {
        if (!sortConfig.key) return dataList;

        return [...dataList].sort((a, b) => {
            const aValue =
                sortConfig.key === "ltp"
                    ? fincodeData?.[a]?.nseLtpPrice ?? fincodeData?.[a]?.bseLtpPrice
                    : sortConfig.key === "change"
                    ? getPriceChangePercentage(a)
                    : fincodeData?.[a]?.netReturns?.[sortConfig.key];
            const bValue =
                sortConfig.key === "ltp"
                    ? fincodeData?.[b]?.nseLtpPrice ?? fincodeData?.[b]?.bseLtpPrice
                    : sortConfig.key === "change"
                    ? getPriceChangePercentage(b)
                    : fincodeData?.[b]?.netReturns?.[sortConfig.key];

            if (aValue == null || bValue == null) return 0;

            return sortConfig.direction === "asc" ? aValue - bValue : bValue - aValue;
        });
    };

    const filteredData = useMemo(() => {
        let combinedData = [];
        if (filter === "topGainers") {
            combinedData = [...(data?.topGainers ?? [])];
        } else if (filter === "topLosers") {
            combinedData = [...(data?.topLosers ?? [])];
        } else {
            combinedData = [...(data?.topGainers ?? []), ...(data?.topLosers ?? [])];
        }

        combinedData = sortedData(combinedData);

        return combinedData.filter((ele) => {
            const symbol = fincodeData?.[ele]?.nseSymbol ?? fincodeData?.[ele]?.bseSymbol;
            return !searchText || searchText.length < 3 || symbol?.toLowerCase().includes(searchText.toLowerCase());
        });
    }, [filter, searchText, sortConfig, data, fincodeData]);

    const filterOptions = [
        { key: "All", value: "all" },
        { key: "Gainers", value: "topGainers" },
        { key: "Losers", value: "topLosers" }
    ];

    const calculateReturnPercentage = (currentPrice, previousPrice) => {
        if (currentPrice != null && previousPrice != null && previousPrice !== 0) {
            return (((currentPrice - previousPrice) / previousPrice) * 100).toFixed(2);
        }
        return null;
    };

    return (
        <div data-aos="xxxx-xx" className="mx-6 mt-8">
            <p className="text-[#202020] text-base font-semibold leading-7 ">
                {stocksDetail?.nseSymbol ?? stocksDetail?.bseSymbol} Companies
            </p>
            <div className="flex justify-start py-4 space-x-4 text-center text-xs font-bold leading-5">
                {filterOptions.map((option, i) => (
                    <button
                        key={i}
                        className={cn(
                            " max-w-fit flex h-8 justify-between items-center flex-[1_0_0] border-[#202020] px-4 py-2 rounded-full border-solid text-xs font-bold leading-5 whitespace-nowrap border",
                            {
                                "bg-black text-white": option.value === filter,
                                "bg-white text-black": option.value !== filter
                            }
                        )}
                        onClick={() => {
                            Mixpanel.track("chip_clicked", {
                                page: "Index_sdp",
                                chip: option.key,
                                tab: isStockPage ? "Stocks" : "Overview"
                            });
                            setFilter(option.value);
                        }}
                    >{`${option.key}${
                        option.value === "topGainers"
                            ? ` (${topGainersLength})`
                            : option.value === "topLosers"
                            ? ` (${topLosersLength})`
                            : ""
                    }`}</button>
                ))}
            </div>

            <div className="flex flex-row font-Inter w-full rounded-2xl overflow-hidden border-[#f5f5f5] border">
                <table className="table border-collapse swiper-no-swiping w-full">
                    <thead>
                        <tr className="h-10">
                            <th className="sticky left-0 bg-[#F5F5F5] min-w-[130px] text-left px-4 py-1 w-[72px] text-[#606060] border-[#f5f5f5] border-b-[1px] text-xs font-medium leading-5">
                                <div className="flex w-full h-6 justify-between items-center rounded bg-[#FFF] pl-1 pr-1.5 py-1">
                                    <input
                                        placeholder="search stocks"
                                        type="search"
                                        className="w-full"
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                    />
                                    <BsSearch className="w-[11px] h-[11px]" />
                                </div>
                            </th>
                            <th
                                className="px-2 lg:px-4 font-medium border border-x-0 border-t-0 border-[#f5f5f5] text-left min-w-[70px] flex-[1_0_0] text-[#606060] font-Inter text-xs leading-5 cursor-pointer"
                                onClick={() => {
                                    Mixpanel.track("sort_applied", {
                                        page: "Index_sdp",
                                        widget: "company_table",
                                        tab: "Overview",
                                        sort: "LTP"
                                    });
                                    handleSort("ltp");
                                }}
                            >
                                <div className="flex flex-row gap-1 justify-start items-center whitespace-nowrap">
                                    <p>LTP</p>
                                    <Image
                                        src={getSortIcon("ltp")}
                                        alt="sort-icon"
                                        className={cn("w-3 h-3", { "rotate-180": sortConfig.direction !== "asc" })}
                                    />
                                </div>
                            </th>
                            <th
                                className="px-2 lg:px-4 font-medium border border-x-0 border-t-0 border-[#f5f5f5] text-left min-w-[70px] flex-[1_0_0] text-[#606060] font-Inter text-xs leading-5 cursor-pointer"
                                onClick={() => {
                                    Mixpanel.track("sort_applied", {
                                        page: "Index_sdp",
                                        widget: "company_table",
                                        tab: "Overview",
                                        sort: "change"
                                    });
                                    handleSort("change");
                                }}
                            >
                                <div className="flex flex-row gap-1 justify-start items-center whitespace-nowrap">
                                    <p>Change (%)</p>
                                    <Image
                                        src={getSortIcon("change")}
                                        alt="sort-icon"
                                        className={cn("w-3 h-3", { "rotate-180": sortConfig.direction !== "asc" })}
                                    />
                                </div>
                            </th>
                            {["1M(%)", "3M(%)", "6M(%)", "1Y(%)", "5Y(%)"].map((period) => (
                                <th
                                    key={period}
                                    className="px-2 lg:px-4 font-medium border border-x-0 border-t-0 border-[#f5f5f5] text-left min-w-[80px] text-[#606060] font-Inter text-xs leading-5 cursor-pointer"
                                    onClick={() => {
                                        Mixpanel.track("sort_applied", {
                                            page: "Index_sdp",
                                            widget: "company_table",
                                            tab: "Overview",
                                            sort: period
                                        });
                                        handleSort(period);
                                    }}
                                >
                                    <div className="flex flex-row gap-1 justify-start items-center whitespace-nowrap ">
                                        <p>{period}</p>
                                        <Image
                                            src={getSortIcon(period)}
                                            alt="sort-icon"
                                            className={cn("w-3 h-3", { "rotate-180": sortConfig.direction !== "asc" })}
                                        />
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr className="m-4 bg-[#D9D9D9] w-full rounded-lg h-[560px] animate-pulse border-[#f5f5f5]" />
                        ) : filteredData.length > 0 ? (
                            filteredData.slice(0, isStockPage ? undefined : 9).map((ele, i) => (
                                <tr key={i} className="h-[52px]">
                                    <td
                                        className="pt-2 sticky left-0 bg-[#f5f5f5] text-[10px] text-[#1c1c1c] font-medium leading-[18px] px-4 min-w-[120px] lg:min-w-[200px] border-[#f5f5f5] cursor-pointer"
                                        onClick={() => {
                                            Mixpanel.track("stock_clicked", {
                                                page: "Index_sdp"
                                            });
                                            router.push(
                                                Actions.toStockDetail(
                                                    fincodeData?.[ele]?.nseSymbol ?? fincodeData?.[ele]?.bseSymbol,
                                                    fincodeData?.[ele]?.compName
                                                )
                                            );
                                        }}
                                    >
                                        <div className="flex flex-row font-Inter">
                                            <div className="text-xs font-medium">
                                                {fincodeData?.[ele]?.nseSymbol ?? fincodeData?.[ele]?.bseSymbol}
                                            </div>
                                        </div>
                                        <div className="text-[10px] leading-[18px] text-[#979797] line-clamp-1">
                                            {fincodeData?.[ele]?.compName}
                                        </div>
                                    </td>
                                    <td className="text-xs text-[#1c1c1c] font-medium leading-[18px] items-center px-2 lg:px-4 border-x-0 border-[#E5E5E5]">
                                        <LivePriceBox
                                            ltpPrice={
                                                fincodeData?.[ele]?.nseLtpPrice ?? fincodeData?.[ele]?.bseLtpPrice
                                            }
                                            textClass="text-xs font-semibold"
                                        />
                                    </td>
                                    <td className="text-xs text-[#1c1c1c] font-medium leading-[18px] items-center px-2 lg:px-4 border-x-0 border-[#E5E5E5]">
                                        <NseOrBsePrice
                                            nseLtpPrice={fincodeData?.[ele]?.nseLtpPrice}
                                            nseClosePrice={fincodeData?.[ele]?.nseClosePrice}
                                            bseLtpPrice={fincodeData?.[ele]?.bseLtpPrice}
                                            bseClosePrice={fincodeData?.[ele]?.bseClosePrice}
                                            textClass="text-xs font-semibold whitespace-nowrap"
                                        />
                                    </td>
                                    {["1 month", "3 month", "6 month", "1 year", "5 year"].map((period) => (
                                        <td
                                            key={period}
                                            className="text-xs text-[#1c1c1c] font-medium leading-[18px] items-center px-2 lg:px-4 border-x-0 border-[#E5E5E5]"
                                        >
                                            {calculateReturnPercentage(
                                                fincodeData?.[ele]?.nseClosePrice ?? fincodeData?.[ele]?.bseClosePrice,
                                                fincodeData?.[ele]?.netReturns?.[period]
                                            )}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={9} className="text-center py-2">
                                    <div className="mx-auto flex gap-2 items-center justify-center p-6 w-80">
                                        <LuSearchX color="#414141" />
                                        <div className="text-[#414141] text-xs font-bold ">No stocks currently</div>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
